import { type MarketingLocale, useLocalized } from '~/lib/marketing-locale'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/*
 * Billable-rate engine on top (asymmetric, light/dark split), then a
 * "scenarios" strip below: three different cost scenarios and the hourly
 * rate each one produces. No three-card row.
 *
 * Copy is locale-keyed — see the note in `crm-section.tsx`. The English tax
 * line is generic ("Taxes and payroll burden") rather than naming the
 * Brazilian ISS/IRPJ/PIS/COFINS stack, which would mean nothing to a US or
 * UK practice.
 */

type Row = { label: string; value: string }
type Member = { dpm: number; hpd: number; name: string; total: number }

type Scenario = {
  label: string
  notes: string
  costs: string
  rate: string
  final: string
  tone: string
  highlight?: boolean
}

type PricingEngineContent = {
  eyebrow: string
  heading: string
  intro: string
  engineTitle: string
  engineSubtitle: string
  fixedCostsLabel: string
  fixedCosts: readonly Row[]
  fixedTotalLabel: string
  fixedTotalValue: string
  teamLabel: string
  teamColumns: readonly [string, string, string, string]
  team: readonly Member[]
  formulaLabel: string
  formula: string
  rateLabel: string
  rateValue: string
  rateCaption: string
  rateRows: readonly Row[]
  scenariosTitle: string
  scenariosHint: string
  scenarioRateLabel: string
  scenarioMarginLabel: string
  exampleBadge: string
  scenarios: readonly Scenario[]
  post: {
    eyebrow: string
    title: string
    body: string
    projectName: string
    marginBadge: string
    quotedLabel: string
    quotedValue: string
    actualLabel: string
    actualValue: string
    suggestionLead: string
    suggestion: string
  }
}

const CONTENT: Record<MarketingLocale, PricingEngineContent> = {
  en: {
    engineSubtitle: 'Updates in real time as you edit costs and team',
    engineTitle: 'The practice’s billable rate',
    exampleBadge: 'example',
    eyebrow: 'PRICING',
    fixedCosts: [
      { label: 'Salaries and owner’s draw', value: '$18,500' },
      { label: 'Rent and utilities', value: '$3,400' },
      { label: 'Software (AutoCAD, SketchUp, Adobe)', value: '$1,860' },
      { label: 'Accounting', value: '$980' },
      { label: 'Marketing and website', value: '$1,200' },
    ],
    fixedCostsLabel: 'Monthly fixed costs',
    fixedTotalLabel: 'Total fixed',
    fixedTotalValue: '$25,940',
    formula: 'Cost per billable hour = fixed costs ÷ active monthly hours',
    formulaLabel: 'FORMULA',
    heading: 'Knowing what to charge is the hardest job. Now it is just editing.',
    intro:
      'A billable rate calculated from your practice’s real costs. Change one number and see the impact on every fee proposal that has not gone out yet.',
    post: {
      actualLabel: 'Actual',
      actualValue: '142 hours',
      body: 'Compare the hours you quoted against the hours actually logged on the timer. When the variance shows up, Planno already suggests a defense for the next proposal.',
      eyebrow: 'AFTER THE JOB',
      marginBadge: '+22% margin',
      projectName: 'Whitfield House',
      quotedLabel: 'Quoted',
      quotedValue: '164 hours',
      suggestion: 'add 6 hours to millwork detailing on the next residential proposals.',
      suggestionLead: 'Suggestion:',
      title: 'Did the margin close where it should? Planno tells you, project by project.',
    },
    rateCaption: 'per hour of studio time',
    rateLabel: 'Cost per billable hour',
    rateRows: [
      { label: 'Taxes and payroll burden', value: '13.45%' },
      { label: 'Suggested margin reserve', value: '+20%' },
      { label: 'Client-facing hourly rate', value: '$79.80' },
    ],
    rateValue: '$54.04',
    scenarioMarginLabel: 'with +20% margin',
    scenarioRateLabel: 'Billable rate',
    scenarios: [
      {
        costs: '$8,200 in fixed costs',
        final: '$42.50',
        label: 'Solo · starting out',
        notes: '1 architect · 160 h/month',
        rate: '$28.80',
        tone: '#5b8def',
      },
      {
        costs: '$25,940 in fixed costs',
        final: '$79.80',
        highlight: true,
        label: 'Studio · 3 people',
        notes: '2 architects + 1 assistant · 472 h/month',
        rate: '$54.04',
        tone: '#7a6cb6',
      },
      {
        costs: '$62,800 in fixed costs',
        final: '$73.60',
        label: 'Practice · 8 people',
        notes: '6 architects + 2 junior · 1,260 h/month',
        rate: '$49.84',
        tone: '#3e7d33',
      },
    ],
    scenariosHint: 'Drag the parameters and watch the rate move',
    scenariosTitle: 'What if your practice looked like this?',
    team: [
      { dpm: 22, hpd: 8, name: 'Maya', total: 176 },
      { dpm: 22, hpd: 8, name: 'Ryan', total: 176 },
      { dpm: 20, hpd: 6, name: 'Chloe', total: 120 },
    ],
    teamColumns: ['Person', 'h/day', 'days', 'h/month'],
    teamLabel: 'Active team',
  },
  'pt-BR': {
    engineSubtitle: 'Atualiza em tempo real conforme você edita custos e equipe',
    engineTitle: 'Hora técnica do escritório',
    exampleBadge: 'exemplo',
    eyebrow: 'PRECIFICAÇÃO',
    fixedCosts: [
      { label: 'Salários e pró-labore', value: 'R$ 18.500' },
      { label: 'Aluguel e contas', value: 'R$ 3.400' },
      { label: 'Softwares (AutoCAD, SketchUp, Adobe)', value: 'R$ 1.860' },
      { label: 'Contabilidade', value: 'R$ 980' },
      { label: 'Marketing e site', value: 'R$ 1.200' },
    ],
    fixedCostsLabel: 'Custos fixos mensais',
    fixedTotalLabel: 'Total fixo',
    fixedTotalValue: 'R$ 25.940',
    formula: 'Custo da hora técnica = custos fixos ÷ horas mensais ativas',
    formulaLabel: 'FÓRMULA',
    heading: 'Saber quanto cobrar é o trabalho mais difícil. Agora é só editar.',
    intro:
      'Hora técnica calculada a partir dos custos reais do seu escritório. Mude um número e veja o impacto em cada orçamento que ainda não saiu.',
    post: {
      actualLabel: 'Executado',
      actualValue: '142 horas',
      body: 'Compare as horas orçadas com as efetivamente lançadas no timer. Quando o desvio aparece, ele já sugere uma defesa para a próxima proposta.',
      eyebrow: 'PÓS-OBRA',
      marginBadge: '+22% margem',
      projectName: 'Residência Lopes',
      quotedLabel: 'Orçado',
      quotedValue: '164 horas',
      suggestion:
        'aumentar em 6h o detalhamento de marcenaria nas próximas propostas residenciais.',
      suggestionLead: 'Sugestão:',
      title: 'A margem fechou onde devia? O Planno te conta, projeto por projeto.',
    },
    rateCaption: 'por hora de trabalho do estúdio',
    rateLabel: 'Custo da hora técnica',
    rateRows: [
      { label: 'Impostos (ISS + IRPJ + PIS/COFINS)', value: '13,45%' },
      { label: 'Reserva de margem sugerida', value: '+20%' },
      { label: 'Hora cliente final', value: 'R$ 79,80' },
    ],
    rateValue: 'R$ 54,04',
    scenarioMarginLabel: 'com margem +20%',
    scenarioRateLabel: 'Hora técnica',
    scenarios: [
      {
        costs: 'R$ 8.200 de custos fixos',
        final: 'R$ 42,50',
        label: 'Solo · iniciando',
        notes: '1 arquiteto · 160 h/mês',
        rate: 'R$ 28,80',
        tone: '#5b8def',
      },
      {
        costs: 'R$ 25.940 de custos fixos',
        final: 'R$ 79,80',
        highlight: true,
        label: 'Estúdio · 3 pessoas',
        notes: '2 plenos + 1 estagiária · 472 h/mês',
        rate: 'R$ 54,04',
        tone: '#7a6cb6',
      },
      {
        costs: 'R$ 62.800 de custos fixos',
        final: 'R$ 73,60',
        label: 'Equipe · 8 pessoas',
        notes: '6 plenos + 2 jr · 1.260 h/mês',
        rate: 'R$ 49,84',
        tone: '#3e7d33',
      },
    ],
    scenariosHint: 'Arraste os parâmetros e veja a hora mudar',
    scenariosTitle: 'E se o seu escritório fosse assim?',
    team: [
      { dpm: 22, hpd: 8, name: 'Marina', total: 176 },
      { dpm: 22, hpd: 8, name: 'Rafael', total: 176 },
      { dpm: 20, hpd: 6, name: 'Camila', total: 120 },
    ],
    teamColumns: ['Pessoa', 'h/dia', 'dias', 'h/mês'],
    teamLabel: 'Equipe ativa',
  },
}

const Engine = ({ content }: { content: PricingEngineContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#ececec] bg-white shadow-[0_24px_60px_-26px_rgba(60,30,90,0.28)]">
    <div className="border-[#f1f1f1] border-b px-5 py-3">
      <p className="font-semibold text-[#1c1c20] text-[13px]">{content.engineTitle}</p>
      <p className="text-[#9a9aa0] text-[11px]">{content.engineSubtitle}</p>
    </div>

    <div className="grid grid-cols-[1.1fr_1fr]">
      <div className="border-[#f1f1f1] border-r p-5">
        <p className="font-semibold text-[#1c1c20] text-[11.5px]">{content.fixedCostsLabel}</p>
        <div className="mt-2 space-y-1.5">
          {content.fixedCosts.map((row) => (
            <div className="flex items-center justify-between text-[11.5px]" key={row.label}>
              <span className="text-[#43434a]">{row.label}</span>
              <span className="font-medium text-[#1c1c20]">{row.value}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between rounded-md bg-[#f6f4fc] px-2.5 py-2">
          <span className="font-semibold text-[#43434a] text-[11.5px]">
            {content.fixedTotalLabel}
          </span>
          <span className="font-bold text-[#1c1c20] text-[13px]">{content.fixedTotalValue}</span>
        </div>
        <p className="mt-4 font-semibold text-[#1c1c20] text-[11.5px]">{content.teamLabel}</p>
        <div className="mt-2 overflow-hidden rounded-lg border border-[#efefef]">
          <div className="grid grid-cols-[1fr_0.6fr_0.6fr_0.6fr] bg-[#fafafa] px-2 py-1.5 text-[#9a9aa0] text-[9.5px]">
            {content.teamColumns.map((column) => (
              <span key={column}>{column}</span>
            ))}
          </div>
          {content.team.map((m) => (
            <div
              className="grid grid-cols-[1fr_0.6fr_0.6fr_0.6fr] border-[#f4f4f4] border-t px-2 py-1.5 text-[11px]"
              key={m.name}
            >
              <span className="font-medium text-[#43434a]">{m.name}</span>
              <span className="text-[#6c6c72]">{m.hpd}</span>
              <span className="text-[#6c6c72]">{m.dpm}</span>
              <span className="font-semibold text-[#1c1c20]">{m.total}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#f6f4fc] to-white p-5">
        <p className="text-[#7a6cb6] text-[10px] tracking-[0.18em]">{content.formulaLabel}</p>
        <p className="mt-2 font-semibold text-[#1c1c20] text-[14px] leading-snug">
          {content.formula}
        </p>
        <div className="mt-4 rounded-xl bg-white p-4 shadow-sm">
          <p className="text-[#9a9aa0] text-[11px]">{content.rateLabel}</p>
          <p className="mt-1 font-bold text-[#1c1c20] text-[32px] leading-none">
            {content.rateValue}
          </p>
          <p className="mt-1 text-[#7a6cb6] text-[11px]">{content.rateCaption}</p>
        </div>
        <div className="mt-4 space-y-2">
          {content.rateRows.map((row, i) => (
            <div
              className="flex items-center justify-between rounded-lg border border-[#e7e3f4] bg-white px-3 py-2 text-[11.5px]"
              key={row.label}
            >
              <span className="text-[#43434a]">{row.label}</span>
              <span className={i === 2 ? 'font-bold text-[#7a6cb6]' : 'font-medium text-[#1c1c20]'}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
)

const ScenarioStrip = ({ content }: { content: PricingEngineContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#e7e3f4] bg-white">
    <div className="flex items-center justify-between border-[#f1f1f1] border-b px-6 py-3">
      <p className="font-semibold text-[#1c1c20] text-[12.5px]">{content.scenariosTitle}</p>
      <p className="text-[#9a9aa0] text-[11px]">{content.scenariosHint}</p>
    </div>
    <div className="grid gap-0 md:grid-cols-3">
      {content.scenarios.map((s, i) => (
        <div
          className={`p-6 ${i > 0 ? 'border-[#f1f1f1] border-t md:border-t-0 md:border-l' : ''} ${s.highlight ? 'bg-[#f6f4fc]' : ''}`}
          key={s.label}
        >
          <div className="flex items-center gap-2">
            <span className="size-[10px] rounded-full" style={{ background: s.tone }} />
            <span className="font-semibold text-[#1c1c20] text-[13px]">{s.label}</span>
            {s.highlight ? (
              <span className="ml-auto rounded-full bg-[#7a6cb6] px-2 py-0.5 font-medium text-[10px] text-white">
                {content.exampleBadge}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-[#6c6c72] text-[12px]">{s.notes}</p>
          <p className="text-[#9a9aa0] text-[11px]">{s.costs}</p>

          <div className="mt-4 rounded-xl border border-[#ececec] bg-white p-3">
            <p className="text-[#9a9aa0] text-[10.5px]">{content.scenarioRateLabel}</p>
            <p className="font-bold text-[#1c1c20] text-[24px] leading-none">{s.rate}</p>
            <div className="mt-2 flex items-center justify-between border-[#f4f4f4] border-t pt-2">
              <span className="text-[#6c6c72] text-[10.5px]">{content.scenarioMarginLabel}</span>
              <span className="font-semibold text-[11.5px]" style={{ color: s.tone }}>
                {s.final}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
)

export const PricingEngineSection = ({
  headingLevel: Heading = 'h2',
}: {
  /** `h1` when the section opens a deep-dive page and owns its top heading. */
  headingLevel?: 'h1' | 'h2'
}) => {
  const content = useLocalized(CONTENT)
  // Sub-headings sit one level below the section heading so the document
  // outline never skips a level (h1 → h2 on the finances page, h2 → h3 elsewhere).
  const SubHeading = Heading === 'h1' ? 'h2' : 'h3'

  return (
    <section className="relative overflow-hidden bg-white py-28">
      <TruchetField
        className="absolute inset-0 h-full w-full text-[#0b0b0d] opacity-[0.05]"
        seed="pricing-engine"
      />
      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <p className="text-[#7a6cb6] text-[12px] tracking-[0.2em]">{content.eyebrow}</p>
          <Heading className="mx-auto mt-3 max-w-[820px] font-display text-[#0b0b0d] text-[clamp(40px,6vw,58px)] leading-[1.06] tracking-[-0.01em]">
            {content.heading}
          </Heading>
          <p className="mx-auto mt-5 max-w-[640px] text-[#6c6c72] text-[clamp(16px,2.2vw,20px)]">
            {content.intro}
          </p>
        </ScrollReveal>

        <ScrollReveal className="mt-14" delay={100}>
          <Engine content={content} />
        </ScrollReveal>

        <ScrollReveal className="mt-8" delay={140}>
          <ScenarioStrip content={content} />
        </ScrollReveal>

        {/* Margin closure, quoted vs. executed, as a wide single band */}
        <ScrollReveal className="mt-8" delay={180}>
          <div className="rounded-2xl bg-[#1c1c20] p-8 text-white md:p-10">
            <div className="grid items-center gap-8 md:grid-cols-[1.2fr_1fr]">
              <div>
                <p className="text-[#d96fa3] text-[11px] tracking-[0.2em]">
                  {content.post.eyebrow}
                </p>
                <SubHeading className="mt-2 font-semibold text-[24px] leading-snug">
                  {content.post.title}
                </SubHeading>
                <p className="mt-3 text-[#a8a3b4] text-[14.5px] leading-relaxed">
                  {content.post.body}
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-5">
                <div className="flex items-baseline justify-between">
                  <span className="text-[11px] text-white/60">{content.post.projectName}</span>
                  <span className="rounded bg-[#dfeedb] px-1.5 py-0.5 font-bold text-[#3e7d33] text-[11px]">
                    {content.post.marginBadge}
                  </span>
                </div>
                <div className="mt-3 space-y-2">
                  <div>
                    <div className="flex items-center justify-between text-[12px]">
                      <span className="text-white/70">{content.post.quotedLabel}</span>
                      <span className="font-semibold">{content.post.quotedValue}</span>
                    </div>
                    <span className="mt-1 block h-1.5 w-full rounded-full bg-white/10">
                      <span className="block h-full w-full rounded-full bg-[#7a6cb6]" />
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-[12px]">
                      <span className="text-white/70">{content.post.actualLabel}</span>
                      <span className="font-semibold">{content.post.actualValue}</span>
                    </div>
                    <span className="mt-1 block h-1.5 w-full rounded-full bg-white/10">
                      <span className="block h-full w-[86%] rounded-full bg-[#7bb86b]" />
                    </span>
                  </div>
                </div>
                <p className="mt-4 rounded-lg bg-white/[0.04] p-2.5 text-[11.5px] text-white/80">
                  ✦ <span className="font-semibold text-white">{content.post.suggestionLead}</span>{' '}
                  {content.post.suggestion}
                </p>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  )
}
